import type {
  IPaginationParams,
  IPaginationResponse,
  ISuccessApiResponse,
} from '@wyz/types';
import {
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  restrictApi,
  serialize,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { useCallback } from 'react';
import { useApi, useConfig } from '~/common/context';
import { PurchaseQueryKeys } from '~/purchase/api';
import { usePurchaseContext } from '~/purchase/context';
import { COMPUTED_PRICING_POLICY_STATUS } from '~/common/schemas';
import type {
  ICreateQuotePricingPolicyPayload,
  IDuplicatePolicyItem,
  IDuplicatePolicyPayload,
  IEditQuotePricingPolicyVariables,
  IGetOneQuotePricingPolicy,
  IQuoteErrorResponse,
  IQuotePricingPolicy,
  IQuotePricingPolicyFilterQuery,
} from '~/purchase/types';
import { CommonPricingPolicyHelpers } from '~/purchase/helpers';

type Options = {
  create?: UseMutationOptions<
    ISuccessApiResponse,
    IQuoteErrorResponse,
    ICreateQuotePricingPolicyPayload
  >;
  edit?: UseMutationOptions<
    ISuccessApiResponse,
    IQuoteErrorResponse,
    IEditQuotePricingPolicyVariables
  >;
  paginationFetcher?: {
    enabled?: boolean;
  };
  remove?: UseMutationOptions<
    ISuccessApiResponse,
    IQuoteErrorResponse,
    { policyGroupId: number }
  >;
  checkOverlap?: UseMutationOptions<
    ISuccessApiResponse,
    IQuoteErrorResponse,
    {
      policyGroupId?: number;
      customerIds: Array<string>;
      startDate: string;
      endDate?: string | null;
      default: boolean;
    }
  >;
  get?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IGetOneQuotePricingPolicy>,
      IQuoteErrorResponse
    >
  > & {
    args: {
      id: string;
    };
  };
  duplicate?: UseMutationOptions<
    ISuccessApiResponse,
    IQuoteErrorResponse,
    { id: string; body: IDuplicatePolicyPayload }
  >;

  getConflicted?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<IDuplicatePolicyItem>>,
      IQuoteErrorResponse
    >
  > & {
    args: {
      id: number;
      customerIds: Array<string>;
    };
  };
  getActivePricingPolicies?: {
    enabled?: boolean;
  };
};

export const useManageQuotePricingPolicies = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { tenantCode } = useConfig();
  const { callCoreApi, pricingPoliciesDomainBaseUrl } = useApi();
  const { state } = usePurchaseContext();
  const idCustomer = state.targetCustomer?.id as string;

  const createMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/pricing-policies`,
        { method: 'POST', body: JSON.stringify(variables) },
      ),
    keysToInvalidateOnSuccess: (variables) => [
      ...variables.criteria
        .filter(
          (criteria) =>
            criteria.code === CommonPricingPolicyHelpers.CUSTOMER_CRITERIA_CODE,
        )
        .map((criteria) =>
          PurchaseQueryKeys.quotePricingPolicies.base(criteria.value),
        ),
      PurchaseQueryKeys.quoteDefaultPricingPolicies.base(),
    ],
    retry: false,
    ...options.create,
  });
  const editMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/pricing-policies/${variables.id}`,
        { method: 'PUT', body: JSON.stringify(variables.body) },
      ),
    keysToInvalidateOnSuccess: (variables) => [
      ...variables.body.criteria
        .filter(
          (criteria) =>
            criteria.code === CommonPricingPolicyHelpers.CUSTOMER_CRITERIA_CODE,
        )
        .map((criteria) =>
          PurchaseQueryKeys.quotePricingPolicies.base(criteria.value),
        ),
      PurchaseQueryKeys.quoteDefaultPricingPolicies.base(),
      [
        ...(idCustomer
          ? PurchaseQueryKeys.quoteServicePricingPolicies.active(idCustomer)
          : []),
      ],
    ],
    retry: false,
    ...options.edit,
  });
  const duplicateMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/pricing-policies/${variables.id}/duplicate`,
        { method: 'POST', body: JSON.stringify(variables.body) },
      ),
    keysToInvalidateOnSuccess: (variables) =>
      variables.body.customers.map((item) =>
        PurchaseQueryKeys.quotePricingPolicies.base(item.id),
      ),
    retry: false,
    ...options.duplicate,
  });

  const paginationFetcher = useCallback(
    (query: IPaginationParams & IQuotePricingPolicyFilterQuery) => {
      return callCoreApi<IPaginationResponse<IQuotePricingPolicy>>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/pricing-policies?${serialize(
          { ...query },
        )}`,
      );
    },
    [callCoreApi, pricingPoliciesDomainBaseUrl, tenantCode],
  );

  const removeMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/pricing-policies/${variables.policyGroupId}`,
        { method: 'DELETE' },
      ),
    keysToInvalidateOnSuccess: [
      PurchaseQueryKeys.quotePricingPolicies.base(idCustomer),
      PurchaseQueryKeys.quoteDefaultPricingPolicies.base(),
      [
        ...(idCustomer
          ? PurchaseQueryKeys.quotePricingPolicies.active(idCustomer)
          : []),
      ],
    ],
    ...options.remove,
  });

  const checkOverlapMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/pricing-policies/validations/overlap`,
        { method: 'POST', body: JSON.stringify(variables) },
      ),
    ...options.checkOverlap,
  });

  const getQuery = useQuery({
    queryKey: PurchaseQueryKeys.quotePricingPolicies.detail(
      options.get?.args.id as string,
    ),
    queryFn: () => {
      return callCoreApi<ISuccessApiResponse<IGetOneQuotePricingPolicy>>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/pricing-policies/${options.get?.args.id}`,
      );
    },
    refetchOnWindowFocus: false,
    ...options.get,
    enabled: Boolean(options.get?.args.id) && options.get?.enabled,
  });
  const getConflictedQuery = useQuery({
    queryKey: PurchaseQueryKeys.quotePricingPolicies.conflicted(
      options.getConflicted?.args,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<IDuplicatePolicyItem>>>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/pricing-policies/${
          options.getConflicted?.args.id
        }/conflicted?${serialize({
          customerIds: options.getConflicted?.args.customerIds,
        })}`,
      ),
    refetchOnWindowFocus: false,
    ...options.getConflicted,
    enabled:
      Boolean(options.getConflicted?.args.id) &&
      Boolean(options.getConflicted?.args.customerIds.length) &&
      Boolean(idCustomer),
  });

  const getActiveQuoteQuery = useQuery({
    queryKey: PurchaseQueryKeys.quotePricingPolicies.active(idCustomer),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<IQuotePricingPolicy>>>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/pricing-policies?${serialize(
          {
            customerIds: [idCustomer],
            page: 1,
            // we cannot have more than 2 actives policies ( default & by customer)
            limit: 2,
            computedStatuses: [COMPUTED_PRICING_POLICY_STATUS.ACTIVE],
          },
        )}`,
      ),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: true,
  });

  return restrictApi(options, {
    create: createMutation,
    edit: editMutation,
    remove: removeMutation,
    get: getQuery,
    getConflicted: getConflictedQuery,
    duplicate: duplicateMutation,
    checkOverlap: checkOverlapMutation,
    getActivePricingPolicies: getActiveQuoteQuery,
    paginationFetcher,
  });
};
